import * as R from "ramda";
import { useState } from "react";
import { Context } from "common/types/context";
import { Label } from "common/widgets/label";
import { getSiteNamesFromValue } from "common/widgets/site-selector/functions";
import { SiteSelectorTreeModal } from "common/widgets/site-selector/site-tree-modal";
import { getSiteLabelByName } from "x/account-settings/sites/functions";
import { AlertWarning } from "../alert";

type SitesLabelProps<T extends string | number> = {
  context: Context;
  value: T[] | T;
  textToHighlight?: string;
  maxVisibleSites?: number;
};

export const SitesLabel = <T extends string | number>({
  context,
  value,
  textToHighlight,
  maxVisibleSites,
}: SitesLabelProps<T>) => {
  const [isTreeSelectorOpen, setIsTreeSelectorOpen] = useState(false);
  const { site, sites } = context;
  const { valueSites, unrecognizedValues } = getSiteNamesFromValue(
    value,
    sites,
  );
  const hasValue = valueSites.length > 0 || unrecognizedValues.length > 0;
  const selected = valueSites.find((s) => s === site.name) ?? valueSites[0];
  const showMultipleSites = !!maxVisibleSites;

  const getSiteLabels = () => {
    const getLabel = (site: string) => getSiteLabelByName(context.sites, site);

    if (!showMultipleSites) return [getLabel(selected)];
    const visibleSites = valueSites.slice(0, maxVisibleSites).map(getLabel);

    return visibleSites;
  };

  const siteLabels = getSiteLabels();
  const label = siteLabels.join(", ");

  const sitesCounter = Math.max(
    valueSites.length + unrecognizedValues.length - siteLabels.length,
    0,
  );

  const handleOpen = () => setIsTreeSelectorOpen(true);
  const handleClose = () => setIsTreeSelectorOpen(false);

  const warningMessage = unrecognizedValues.length ? (
    <AlertWarning
      className="x-margin-bottom-10-i"
      message={_(
        "You do not have enough permission to view some of the assigned sites",
      )}
    />
  ) : undefined;

  return (
    <div className="x-multiple-labels">
      <div>
        {!hasValue || label ? (
          <Label textToHighlight={textToHighlight} value={label} />
        ) : (
          <Label
            className="x-italic"
            textToHighlight={textToHighlight}
            value={_("[Restricted]")}
          />
        )}
      </div>
      {sitesCounter && valueSites.length > 1 ? (
        <div
          className="x-summary-more"
          onClick={handleOpen}
        >{`+${sitesCounter}`}</div>
      ) : undefined}
      {isTreeSelectorOpen && sites.length > 0 ? (
        <SiteSelectorTreeModal
          sites={sites}
          isSiteDisabled={R.T}
          isExpandable={true}
          includeGroupSites={true}
          allowMultipleSelection={true}
          hideFooter={true}
          headerComponent={warningMessage}
          onClose={handleClose}
          value={valueSites}
        />
      ) : undefined}
    </div>
  );
};
